import { useReducer, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import {
  ApplicationType,
  ENTITY_NOT_FOUND_MESSAGE,
  UNAUTHORIZED_MESSAGE,
} from '../constants';
import { getCreditCardApplication } from '../data/queries';

import { logError } from './logging';
import { prefixRoute } from './routes';

// useApplicationPolling
// Fetches application conditionally and periodically using graphQL query getCreditCardApplication
// Used so the frontend can react to realtime changes (status transitions) executed on the backend

// This custom hook returns an object of type PollingState, a union of the following types:
//   IdleState     when the application is not currently being polled
//   ActiveState   when the application is being polled and retrieved successfully
//   FailureState  when there is a networking error fetching the application

export enum PollingStatus {
  IDLE = 'IDLE',
  ACTIVE = 'ACTIVE',
  FAILURE = 'FAILURE',
}

// PollingState types for return value of useApplicationPolling are one-to-one with PollingStatus
type IdleState = { pollingStatus: PollingStatus.IDLE };
type ActiveState = {
  pollingStatus: PollingStatus.ACTIVE;
  application: ApplicationType;
};
type FailureState = { pollingStatus: PollingStatus.FAILURE; error: object };

// PollingAction types for internal pollingReducer are one-to-one with PollingState
type IdleAction = { type: PollingStatus.IDLE };
type ActiveAction = {
  type: PollingStatus.ACTIVE;
  application: ApplicationType;
};
type FailureAction = { type: PollingStatus.FAILURE; error: object };

// Possible return types of useApplicationPolling custom hook
type PollingState = IdleState | ActiveState | FailureState;

// Possible action types of internal pollingReducer
type PollingAction = IdleAction | ActiveAction | FailureAction;

// Update polling status with internal pollingReducer
function pollingReducer(
  _state: PollingState,
  action: PollingAction
): PollingState {
  switch (action.type) {
    case PollingStatus.IDLE: {
      return {
        pollingStatus: PollingStatus.IDLE,
      };
    }
    case PollingStatus.ACTIVE: {
      return {
        pollingStatus: PollingStatus.ACTIVE,
        application: action.application,
      };
    }
    case PollingStatus.FAILURE: {
      return {
        pollingStatus: PollingStatus.FAILURE,
        error: action.error,
      };
    }
    default: {
      throw new Error(`Unhandled action: ${action}`);
    }
  }
}

/**
 * Polls application at constant intervals using graphQL query getCreditCardApplication.
 * Current usage: Periodically check application status while waiting for backend to finish
 * processing workflow events.
 * @param applicationId Application ID of queried application
 * @param polling Start/stop periodic application polling
 * @param interval Time in milliseconds between each query, default is 1000 ms
 * @returns Object of type PollingState, which can include latest polling response
 */
export function useApplicationPolling(
  applicationId: string,
  polling: boolean,
  interval = 1000
) {
  const [state, dispatch] = useReducer(pollingReducer, {
    pollingStatus: PollingStatus.IDLE,
  });
  const navigate = useNavigate();

  // Poll the application at constant intervals while props.polling is true
  useEffect(() => {
    const abortController = new AbortController();
    let timer: NodeJS.Timeout;

    // Query the application
    if (polling) {
      const pollApplication = async () => {
        try {
          const response = await getCreditCardApplication(applicationId);
          const { data, errors } = response;
          // Update application if response was successfully retrieved
          if (
            data &&
            data.getCreditCardApplication &&
            !abortController.signal.aborted
          ) {
            // Dispatch 'ACTIVE' action when application was retrieved successfully
            dispatch({
              type: PollingStatus.ACTIVE,
              application: data.getCreditCardApplication,
            });
            // Continue polling application
            timer = setTimeout(() => {
              // Query the application again after delay
              pollApplication();
            }, interval);
          }
          if (errors && !abortController.signal.aborted) {
            // if they tried to fetch an application they don't own
            if (errors[0] && errors[0].code === ENTITY_NOT_FOUND_MESSAGE) {
              // just drop them off at the apply page
              // this will resume their proper app
              navigate(prefixRoute(''), { replace: true });
            }
            // Dispatch 'FAILURE' action when there was an error from graphQL
            dispatch({
              type: PollingStatus.FAILURE,
              error: errors,
            });
          }
        } catch (err: any) {
          if (!abortController.signal.aborted) {
            // Dispatch 'FAILURE' action when the application request failed
            dispatch({
              type: PollingStatus.FAILURE,
              error: new Error(err.message),
            });
            if (err.message !== UNAUTHORIZED_MESSAGE) {
              logError(err, {
                errorName: err.name || 'UtilityError',
                errorMessage: err.message || 'Unexpected error occurred',
                component: 'useApplicationPolling',
                function: 'pollApplication',
              });
            }
          }
        }
      };

      // Query the application the first time
      pollApplication();
    } else if (!polling && !abortController.signal.aborted) {
      // Dispatch 'IDLE' action while application is not being polled
      dispatch({
        type: PollingStatus.IDLE,
      });
    }

    return () => {
      abortController.abort();
      clearTimeout(timer);
    };
  }, [polling]);

  // Return the current PollingState
  return [{ ...state }];
}
